'use client';

/**
 * Empty states pentru SoftFactura dashboard widgets.
 *
 * Estetica: drafting paper / blueprint editorial. Ghost axes, crosshair-uri
 * de origine, phantom rows. Microcopy factual, fără emoji sau exclamații.
 * Tipografia metricii reale e păstrată — placeholder-ul "—" e în Poppins 22
 * exact ca valoarea, ca să fie continuitate vizuală cu starea cu date.
 *
 * Convenții folosite:
 *   - bg-transparent (cream-ul vine din fundal)
 *   - border: #E8E4D8 (baseline) / #F0ECE0 (subtle)
 *   - muted text: #9A9791
 *   - Poppins 22 / Poppins 9.5-11 uppercase tracking-[0.06em]
 */

import type { LucideIcon } from 'lucide-react';
import { AlertTriangle, ArrowUpRight, Check, Inbox, Plug, RefreshCw } from 'lucide-react';

const FRAUNCES = 'var(--font-poppins), sans-serif'; // fost Fraunces — Poppins din 2026-07-22 (consecventa dashboard)
const MONO = 'var(--font-poppins), sans-serif'; // fost JetBrains Mono

// ─────────────────────────────────────────────────────────────────────────────
//  EmptyKpi
//  Pentru widget-urile size S/M cu un singur număr (revenue-month, dso,
//  outstanding, overdue-kpi, invoiced-today, collected-today, collected-month).
// ─────────────────────────────────────────────────────────────────────────────

interface EmptyKpiProps {
  label: string;          // "VENIT · LUNĂ"
  unit?: string;          // "RON" / "facturi" / "zile"
  hint?: string;          // "Emite prima factură ca să vezi totalul"
  accent?: string;        // hex pentru bullet-ul de categorie
  ctaLabel?: string;      // "Emite factură"
  ctaHref?: string;
}

export function EmptyKpi({ label, unit, hint, accent = '#9A9791', ctaLabel, ctaHref }: EmptyKpiProps) {
  return (
    <div className="relative h-full px-4 py-3.5 flex flex-col">
      {/* header bullet + label */}
      <div className="flex items-center gap-2 mb-2.5">
        <span
          className="w-2 h-2 rounded-full shrink-0 border-2"
          style={{ borderColor: accent, background: 'transparent' }}
        />
        <span
          className="text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-neutral-500 dark:text-neutral-400"
          style={{ fontFamily: 'var(--font-poppins), sans-serif' }}
        >
          {label}
        </span>
      </div>

      {/* placeholder valoare — păstrează tipografia metricii reale */}
      <div className="flex items-baseline gap-1 mb-1.5">
        <span
          className="leading-none tracking-[-0.02em] text-neutral-300 dark:text-neutral-700 tabular-nums select-none"
          style={{ fontFamily: FRAUNCES, fontSize: '1.375rem', fontWeight: 500 }}
          aria-hidden="true"
        >
          —
        </span>
        {unit && (
          <span
            className="text-[0.625rem] font-medium text-neutral-400 dark:text-neutral-600 align-super"
            style={{ fontFamily: 'var(--font-poppins), sans-serif' }}
          >
            {unit}
          </span>
        )}
      </div>

      {/* baseline tick — sugerează axa Y la zero */}
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="block w-1 h-px bg-neutral-300 dark:bg-neutral-700" />
        <span
          className="text-[0.5938rem] font-medium uppercase tracking-[0.08em] text-neutral-400 dark:text-neutral-600"
          style={{ fontFamily: MONO }}
        >
          0 baseline
        </span>
      </div>

      {hint && (
        <p
          className="text-[0.6875rem] text-neutral-500 dark:text-neutral-400 leading-snug mt-auto pt-2"
          style={{ fontFamily: 'var(--font-poppins), sans-serif' }}
        >
          {hint}
        </p>
      )}

      {ctaLabel && ctaHref && (
        <a
          href={ctaHref}
          className="mt-2 inline-flex items-center gap-1 text-[0.6563rem] font-semibold uppercase tracking-[0.06em] text-neutral-700 dark:text-neutral-200 hover:text-neutral-900 dark:hover:text-white border-b border-dashed border-neutral-400 dark:border-neutral-600 self-start pb-px"
          style={{ fontFamily: MONO }}
        >
          {ctaLabel}
          <ArrowUpRight className="h-2.5 w-2.5" strokeWidth={2.5} />
        </a>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  EmptyChart
//  Pentru line/area charts (revenue-chart, overdue-trend, cashflow-forecast,
//  collection-rate, yoy-comparison). Ghost axes + drafting baseline.
// ─────────────────────────────────────────────────────────────────────────────

interface EmptyChartProps {
  title: string;
  hint: string;            // "Necesită minim 2 luni de date"
  accent?: string;
  ctaLabel?: string;
  ctaHref?: string;
  height?: number;
}

export function EmptyChart({ title, hint, accent = '#9A9791', ctaLabel, ctaHref, height = 240 }: EmptyChartProps) {
  // Y ticks (4 niveluri ghost)
  const yTicks = [0, 1, 2, 3, 4];
  // X ticks (5 puncte)
  const xTicks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div className="relative w-full" style={{ height }}>
      <svg
        viewBox="0 0 1000 320"
        preserveAspectRatio="none"
        className="w-full h-full block"
        aria-hidden="true"
      >
        {/* horizontal grid (matches MultiLineChart pattern) */}
        {yTicks.map((i) => {
          const y = 16 + (i / 4) * (320 - 16 - 32);
          const isBaseline = i === 4;
          return (
            <g key={i}>
              <line
                x1={52}
                y1={y}
                x2={1000 - 20}
                y2={y}
                stroke={isBaseline ? '#E8E4D8' : '#F0ECE0'}
                strokeWidth="1"
                strokeDasharray={isBaseline ? undefined : '2 4'}
              />
              <text
                x={44}
                y={y + 3}
                textAnchor="end"
                fill="#C9C5BB"
                style={{ fontFamily: MONO, fontSize: '0.5938rem', fontWeight: 500 }}
              >
                —
              </text>
            </g>
          );
        })}

        {/* x-axis ticks ghost */}
        {xTicks.map((p, i) => {
          const x = 52 + p * (1000 - 52 - 20);
          return (
            <g key={i}>
              <line x1={x} y1={288} x2={x} y2={292} stroke="#E8E4D8" strokeWidth="1" />
              <text
                x={x}
                y={306}
                textAnchor="middle"
                fill="#C9C5BB"
                style={{ fontFamily: MONO, fontSize: '0.625rem', fontWeight: 500, letterSpacing: '0.06em' }}
              >
                ···
              </text>
            </g>
          );
        })}

        {/* origin crosshair — marker discret de pornire */}
        <g transform="translate(52, 288)">
          <circle r="2.5" fill="none" stroke={accent} strokeWidth="1" opacity="0.5" />
          <line x1="-4" y1="0" x2="4" y2="0" stroke={accent} strokeWidth="0.75" opacity="0.5" />
          <line x1="0" y1="-4" x2="0" y2="4" stroke={accent} strokeWidth="0.75" opacity="0.5" />
        </g>

        {/* phantom data path — un punct pe zero, sugerează "începutul" */}
        <line
          x1={52}
          y1={288}
          x2={1000 - 20}
          y2={288}
          stroke={accent}
          strokeWidth="1"
          strokeDasharray="1 6"
          opacity="0.25"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* overlay text — mid-chart */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div
          className="text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-neutral-400 dark:text-neutral-500 mb-2"
          style={{ fontFamily: MONO }}
        >
          {title}
        </div>
        <div
          className="text-[0.75rem] text-neutral-500 dark:text-neutral-400 max-w-[280px] text-center leading-snug"
          style={{ fontFamily: 'var(--font-poppins), sans-serif' }}
        >
          {hint}
        </div>
        {ctaLabel && ctaHref && (
          <a
            href={ctaHref}
            className="pointer-events-auto mt-3 inline-flex items-center gap-1 text-[0.6563rem] font-semibold uppercase tracking-[0.06em] text-neutral-700 dark:text-neutral-200 hover:text-neutral-900 border-b border-dashed border-neutral-400 dark:border-neutral-600 pb-px"
            style={{ fontFamily: MONO }}
          >
            {ctaLabel}
            <ArrowUpRight className="h-2.5 w-2.5" strokeWidth={2.5} />
          </a>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  EmptyDonut
//  Pentru vat-donut. Inel gol cu "—" în centru.
// ─────────────────────────────────────────────────────────────────────────────

interface EmptyDonutProps {
  label: string;
  hint: string;
  size?: number;
}

export function EmptyDonut({ label, hint, size = 140 }: EmptyDonutProps) {
  const r = size / 2 - 14;
  const c = size / 2;
  return (
    <div className="flex items-center gap-5">
      <svg width={size} height={size} className="shrink-0" aria-hidden="true">
        <circle cx={c} cy={c} r={r} fill="none" stroke="#E8E4D8" strokeWidth="14" />
        <circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke="#C9C5BB"
          strokeWidth="14"
          strokeDasharray="3 8"
          opacity="0.5"
        />
        <text
          x={c}
          y={c + 6}
          textAnchor="middle"
          fill="#C9C5BB"
          style={{ fontFamily: FRAUNCES, fontSize: '1.375rem', fontWeight: 500 }}
        >
          —
        </text>
      </svg>
      <div>
        <div
          className="text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-neutral-400 dark:text-neutral-500 mb-1.5"
          style={{ fontFamily: MONO }}
        >
          {label}
        </div>
        <p
          className="text-[0.75rem] text-neutral-500 dark:text-neutral-400 leading-snug max-w-[200px]"
          style={{ fontFamily: 'var(--font-poppins), sans-serif' }}
        >
          {hint}
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  WidgetErrorState
//  Stare de eroare uniformă pentru TOATE widget-urile (audit 2026-07-13):
//  înainte, la o eroare de API widget-urile rămâneau goale / pe zero, în
//  tăcere — imposibil de deosebit de „nu există date". Mesaj clar + retry.
// ─────────────────────────────────────────────────────────────────────────────

export function WidgetErrorState({ onRetry }: { onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-col items-center justify-center text-center gap-2 py-6">
      <div className="grid h-10 w-10 place-items-center rounded-full bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400">
        <AlertTriangle className="h-5 w-5" strokeWidth={2} aria-hidden />
      </div>
      <p className="text-[0.8438rem] font-semibold text-neutral-800 dark:text-neutral-100 leading-snug">
        Nu am putut încărca datele
      </p>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-snug max-w-[260px] -mt-1">
        Verifică conexiunea sau încearcă din nou.
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 dark:border-neutral-700 px-3 py-1.5 text-xs font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
        >
          <RefreshCw className="h-3 w-3" />
          Reîncearcă
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  EmptyList
//  Pentru list widgets: top-clients, recent-invoices, overdue-invoices,
//  top-debitors, upcoming-recurring, detected-recurring, activity-feed.
//
//  Redesign 2026-07-10 (decizie owner, „varianta A"): vechiul stil „blueprint"
//  cu rânduri fantomă (linii ─── punctate) era citit de utilizatori ca un
//  widget STRICAT / skeleton blocat (raportat pe „Facturi cu scadența
//  depășită"). Acum: icon de stare + mesaj centrat, imposibil de confundat.
//  tone='positive' = gol înseamnă VESTE BUNĂ (bifă verde: fără restanțe,
//  fără debitori); default 'neutral' = pur informativ (încă nu există date).
// ─────────────────────────────────────────────────────────────────────────────

interface EmptyListProps {
  /** Contextul widget-ului — folosit doar pentru accesibilitate (aria-label). */
  title: string;
  /** Mesajul principal (ex. „Nicio factură depășită."). */
  hint: string;
  /** Subtext opțional, o propoziție de context. */
  sub?: string;
  /** 'positive' = gol e de bine (bifă verde); 'neutral' = încă fără date. */
  tone?: 'positive' | 'neutral';
  ctaLabel?: string;
  ctaHref?: string;
}

export function EmptyList({
  title,
  hint,
  sub,
  tone = 'neutral',
  ctaLabel,
  ctaHref,
}: EmptyListProps) {
  const positive = tone === 'positive';
  return (
    <div
      role="status"
      aria-label={title}
      className="flex flex-col items-center justify-center text-center gap-2 py-6"
    >
      <div
        className={
          'grid h-10 w-10 place-items-center rounded-full ' +
          (positive
            ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400'
            : 'bg-neutral-100 text-neutral-400 dark:bg-neutral-800 dark:text-neutral-500')
        }
      >
        {positive ? (
          <Check className="h-5 w-5" strokeWidth={2.5} aria-hidden />
        ) : (
          <Inbox className="h-5 w-5" strokeWidth={2} aria-hidden />
        )}
      </div>
      <p className="text-[0.8438rem] font-semibold text-neutral-800 dark:text-neutral-100 leading-snug">
        {hint}
      </p>
      {sub && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-snug max-w-[260px] -mt-1">
          {sub}
        </p>
      )}
      {ctaLabel && ctaHref && (
        <a
          href={ctaHref}
          className="mt-1 inline-flex items-center gap-1 text-[0.6563rem] font-semibold uppercase tracking-[0.06em] text-neutral-700 dark:text-neutral-200 hover:text-neutral-900 border-b border-dashed border-neutral-400 dark:border-neutral-600 pb-px"
          style={{ fontFamily: MONO }}
        >
          {ctaLabel}
          <ArrowUpRight className="h-2.5 w-2.5" strokeWidth={2.5} />
        </a>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  EmptyConnection
//  Pentru widget-uri de conexiune (anaf-status, spv-status). Două noduri cu
//  link discontinuu — "broken connection" geometric.
// ─────────────────────────────────────────────────────────────────────────────

interface EmptyConnectionProps {
  label: string;             // "STATUS SPV"
  state: string;             // "Neconectat"
  detail: string;            // "Token de acces expirat sau lipsă"
  ctaLabel: string;          // "Conectează SPV"
  ctaHref: string;
  Icon?: LucideIcon;
}

export function EmptyConnection({ label, state, detail, ctaLabel, ctaHref, Icon = Plug }: EmptyConnectionProps) {
  return (
    <div className="relative">
      <div
        className="text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-neutral-400 dark:text-neutral-500 mb-4"
        style={{ fontFamily: MONO }}
      >
        {label}
      </div>

      {/* connection diagram */}
      <div className="flex items-center justify-center gap-4 py-4">
        {/* node 1: tenant (filled) */}
        <div className="flex flex-col items-center gap-1.5">
          <div className="w-9 h-9 rounded-full bg-neutral-200 dark:bg-neutral-800 grid place-items-center">
            <Icon className="h-4 w-4 text-neutral-500 dark:text-neutral-400" strokeWidth={1.75} />
          </div>
          <span
            className="text-[0.5625rem] font-semibold uppercase tracking-[0.08em] text-neutral-400 dark:text-neutral-500"
            style={{ fontFamily: MONO }}
          >
            tenant
          </span>
        </div>

        {/* dashed broken line */}
        <svg width="80" height="20" className="shrink-0" aria-hidden="true">
          <line
            x1="0"
            y1="10"
            x2="32"
            y2="10"
            stroke="#9A9791"
            strokeWidth="1"
            strokeDasharray="3 3"
          />
          {/* break gap with X marker */}
          <g transform="translate(40, 10)">
            <circle r="5" fill="none" stroke="#C2410C" strokeWidth="1" opacity="0.7" />
            <line x1="-3" y1="-3" x2="3" y2="3" stroke="#C2410C" strokeWidth="1" />
            <line x1="-3" y1="3" x2="3" y2="-3" stroke="#C2410C" strokeWidth="1" />
          </g>
          <line
            x1="48"
            y1="10"
            x2="80"
            y2="10"
            stroke="#9A9791"
            strokeWidth="1"
            strokeDasharray="3 3"
            opacity="0.4"
          />
        </svg>

        {/* node 2: ANAF (outline, dimmed) */}
        <div className="flex flex-col items-center gap-1.5">
          <div className="w-9 h-9 rounded-full border border-dashed border-neutral-300 dark:border-neutral-700 grid place-items-center">
            <span
              className="text-[0.625rem] font-bold text-neutral-400 dark:text-neutral-600"
              style={{ fontFamily: MONO }}
            >
              AN
            </span>
          </div>
          <span
            className="text-[0.5625rem] font-semibold uppercase tracking-[0.08em] text-neutral-400 dark:text-neutral-500"
            style={{ fontFamily: MONO }}
          >
            anaf
          </span>
        </div>
      </div>

      <div className="text-center mb-3">
        <div
          className="inline-flex items-center gap-1.5 text-[0.6563rem] font-semibold uppercase tracking-[0.06em] text-orange-700 dark:text-orange-400"
          style={{ fontFamily: MONO }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
          {state}
        </div>
        <p
          className="text-[0.75rem] text-neutral-500 dark:text-neutral-400 leading-snug mt-1.5 max-w-[260px] mx-auto"
          style={{ fontFamily: 'var(--font-poppins), sans-serif' }}
        >
          {detail}
        </p>
      </div>

      <div className="flex justify-center">
        <a
          href={ctaHref}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] rounded-md hover:bg-neutral-800 dark:hover:bg-white transition-colors"
          style={{ fontFamily: MONO }}
        >
          {ctaLabel}
          <ArrowUpRight className="h-3 w-3" strokeWidth={2.5} />
        </a>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Microcopy presets — exporturi pentru reutilizare cross-widget.
// ─────────────────────────────────────────────────────────────────────────────

export const EMPTY_COPY = {
  // KPI tiles
  revenueMonth: { hint: 'Niciun document emis luna aceasta.', cta: 'Emite factură', href: '/facturi/nou' },
  invoicedToday: { hint: 'Nu ai facturat azi.', cta: 'Emite factură', href: '/facturi/nou' },
  collectedToday: { hint: 'Nicio încasare înregistrată azi.', cta: 'Înregistrează încasare', href: '/incasari?nou=1' },
  collectedMonth: { hint: 'Nicio încasare luna aceasta.', cta: 'Înregistrează încasare', href: '/incasari?nou=1' },
  cashPosition: { hint: 'Calculul pornește după prima factură emisă.' },
  outstanding: { hint: 'Toate facturile sunt încasate.' },
  overdueKpi: { hint: 'Nicio factură depășită — termenele sunt respectate.' },
  dso: { hint: 'Necesită minim o factură emisă și încasată.' },
  collectionRate: { hint: 'Necesită cel puțin 2 luni de activitate.' },

  // Charts
  revenueChart: { hint: 'Graficul afișează totalul facturat lunar după prima factură emisă.', cta: 'Emite factură', href: '/facturi/nou' },
  overdueTrend: { hint: 'Tendința apare după ce ai facturi cu scadența depășită.' },
  cashflow: { hint: 'Prognoza folosește facturi emise + recurențe active. Niciuna nu există încă.' },
  yoy: { hint: 'Comparația cere minim 2 luni cu facturat > 0.' },

  // Lists
  topClients: { hint: 'Topul se construiește pe baza facturilor emise.', cta: 'Emite factură', href: '/facturi/nou' },
  topDebitors: { hint: 'Niciun client cu sold restant.' },
  recentInvoices: { hint: 'Niciun document emis încă (factură, proformă, aviz, storno).', cta: 'Emite factură', href: '/facturi/nou' },
  overdueInvoices: { hint: 'Nicio factură depășită.' },
  alerts: { hint: 'Nicio alertă activă în acest moment.' },
  upcomingRecurring: { hint: 'Nu ai recurențe programate. Setează una pe o factură existentă.' },
  detectedRecurring: { hint: 'Sugestiile apar după minim 3 facturi către același client, la intervale similare.' },
  activityFeed: { hint: 'Activitatea recentă apare aici după prima acțiune în cont.' },

  // Connections
  spv: { state: 'Neconectat', detail: 'Token-ul de acces ANAF lipsește sau a expirat.', cta: 'Conectează SPV', href: '/setari/anaf' },
  anaf: { state: 'Neconectat', detail: 'Conectează SPV ca să trimiți facturi prin e-Factura.', cta: 'Conectează SPV', href: '/setari/anaf' },

  // ANAF specific
  vatDonut: { hint: 'Calculul TVA pornește după prima factură cu TVA emisă luna aceasta.' },
} as const;
